import React, { Fragment, useEffect, useState } from 'react';
import { compose } from 'react-apollo';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import {
  AccessibilityWrapper,
  Icon,
  LoadingSpinner,
} from 'legacy-packages/ft-cb';
import StudentsSearch from 'legacy/src/components/impersonating/components/StudentsSearch';
import ImpersonateFocusWrapper from 'legacy/src/components/impersonating/components/ImpersonateFocusWrapper';
import {
  isImpersonating as isImpersonatingLC,
  isImpersonatingStudent,
  withUnitsConnectLean,
  getSectionAndStudentName,
  getLDBImpersonatingHeaderDetailLC,
} from 'legacy/src/components/impersonating/utils';
import {
  setStudentId,
  setSectionId,
  setIsReadOnly,
  setEducationPeriod,
  setSubject,
  setCurrentSubject,
  setTeacherClasses,
} from 'legacy/src/components/impersonating/state/actions';
import { LC_CONSTANTS } from 'legacy/src/components/impersonating/utils/constants';
import useDoImpersonate from 'legacy/src/components/impersonating/hooks/useDoImpersonate';
import useDoLogout from 'legacy/src/components/impersonating/hooks/useDoLogout';
import useTeacherData from 'legacy/src/components/impersonating/hooks/useTeacherData';
import { isLockdownBrowser } from 'legacy/src/components/auth/utils';
import isTeacherTeachingClass from 'legacy/src/components/impersonating/utils/isTeacherTeachingClass';
import getImpersonatedStudentClass from 'legacy/src/components/impersonating/utils/getImpersonatedStudentClass';
import ResponsiveView from 'legacy-src/utility/ResponsiveView';
import { impersonatorFymClient } from 'legacy-src/utility/GraphqlClients';
import './styles.scss';

const ImpersonateHeader = (props) => {
  const [wasRedirected, setWasRedirected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    isImpersonatingFlagPreap,
    isImpersonatingFlagAp,
    impersonating,
    dispatch,
    router,
    userData,
    resetUnitsInternalStore,
    subjectData: { isPreApSubject },
    areFeatureFlagsLoaded,
  } = props;
  const canImpersonate = isPreApSubject
    ? isImpersonatingFlagPreap
    : isImpersonatingFlagAp;
  const { isReadOnly, studentId, sectionId, teacherClasses, teacherSubjects } =
    impersonating;
  const [isImpersonating, setIsImpersonating] = useState(false);
  const {
    location: { pathname },
    params: { subject, assignmentId },
  } = router;

  const [, doImpersonate] = useDoImpersonate({
    dispatch,
    router,
    userData,
    impersonating,
  });

  const [{ navData }, getTeacherData] = useTeacherData({
    dispatch,
    client: impersonatorFymClient,
  });

  const [, doLogout] = useDoLogout({
    dispatch,
    router,
    navData,
    userData,
    impersonating,
    callback: () => {
      resetUnitsInternalStore();
      setIsLoading(false);
    },
  });

  const onStudentSelected = (section, student) => {
    if (student && !isImpersonatingStudent(student)) {
      setIsLoading(true);
      doImpersonate({
        studentId: student.value,
        sectionId: section.value,
        ...getSectionAndStudentName(
          student.value,
          section.value,
          teacherClasses,
        ),
      });
    }
  };

  const onLogoutClick = () => {
    doLogout();
  };

  useEffect(() => {
    if (
      areFeatureFlagsLoaded &&
      isImpersonatingLC() &&
      !canImpersonate &&
      !wasRedirected
    ) {
      setWasRedirected(true);
      const impersonatingSubject = window.localStorage.getItem(
        LC_CONSTANTS.ISUBJECT,
      );
      if (impersonatingSubject) {
        router.replace(`/${impersonatingSubject}/home`);
      }
    }
  }, [areFeatureFlagsLoaded, canImpersonate, wasRedirected]);

  useEffect(() => {
    /**
     * Persist redux store from localstorage on refresh
     * */
    if (isImpersonatingLC() && canImpersonate && !isImpersonating) {
      let sectionIdLC = window.localStorage.getItem(LC_CONSTANTS.SECTION_ID);
      const subjectIdLC = window.localStorage.getItem(LC_CONSTANTS.ISUBJECT);
      let currentSubjectIdLC = window.localStorage.getItem(
        LC_CONSTANTS.ICURRENTSUBJECT,
      );
      if (!isReadOnly) {
        setIsReadOnly(dispatch, true);
        setEducationPeriod(
          dispatch,
          window.localStorage.getItem(LC_CONSTANTS.EDUCATION_PERIOD),
        );
        setSubject(dispatch, subjectIdLC);
        setCurrentSubject(dispatch, currentSubjectIdLC);
      }

      document.querySelector('#root').classList.add('is-impersonating');

      setIsImpersonating(true);

      setIsLoading(true);
      // we don't need the teacher data in LDB mode since we are just showing the banner
      if (isLockdownBrowser()) {
        setIsLoading(false);
      } else {
        getTeacherData({
          subjectId: subjectIdLC,
        }).then(() => {
          setSectionId(dispatch, sectionIdLC);
          setStudentId(
            dispatch,
            window.localStorage.getItem(LC_CONSTANTS.STUDENT_ID),
          );
          setIsLoading(false);
        });
      }
    }
  }, [canImpersonate, isImpersonating, isReadOnly]);

  /**
   * Has support for changing subjects or refreshing a page with or without a subject param
   * */
  useEffect(() => {
    if (subject) {
      setCurrentSubject(dispatch, subject);
      window.localStorage.setItem(LC_CONSTANTS.ICURRENTSUBJECT, subject);
    }

    const selectedSubject = (
      subject ||
      window.localStorage.getItem(LC_CONSTANTS.ICURRENTSUBJECT) ||
      ''
    ).toString();
    if (selectedSubject && teacherSubjects.length > 0) {
      const educationPeriodLC = Number.parseInt(
        window.localStorage.getItem(LC_CONSTANTS.EDUCATION_PERIOD),
        10,
      );
      const teacherSubject =
        (teacherSubjects || []).find(({ id }) => id === selectedSubject) || {};
      const classes = (teacherSubject.classes || []).filter(
        ({ educationPeriod }) => educationPeriod === educationPeriodLC,
      );
      const sectionIdLC = window.localStorage.getItem(LC_CONSTANTS.SECTION_ID);
      const isTeacherTeaching = isTeacherTeachingClass({
        sectionId: sectionIdLC,
        classes: classes || [],
      });
      setTeacherClasses(dispatch, classes);
      if (!isTeacherTeaching && sectionIdLC !== '-1') {
        const studentIdLC = window.localStorage.getItem(
          LC_CONSTANTS.STUDENT_ID,
        );
        const impersonatedStudentClass = getImpersonatedStudentClass({
          studentId: studentIdLC,
          educationPeriod: educationPeriodLC,
          classes,
        });
        if (!isEmpty(impersonatedStudentClass)) {
          const { id: sectionIdFound } = impersonatedStudentClass;
          setSectionId(dispatch, sectionIdFound);
          window.localStorage.setItem(LC_CONSTANTS.SECTION_ID, sectionIdFound);
        }
      }
    }
  }, [subject, teacherSubjects]);

  if (!isImpersonating) return null;

  const icon = (
    <Fragment>
      <ResponsiveView screens={['mobile']}>
        <Icon
          className={cn('Impersonate--logout-icon')}
          name="arrow_assigned"
          width="1.4rem"
          height="1.4rem"
        />
      </ResponsiveView>
      <ResponsiveView screens={['desktop']}>
        <Icon
          className={cn('Impersonate--logout-icon')}
          name="arrow_assigned"
          width="1.2rem"
          height="1.2rem"
        />
      </ResponsiveView>
    </Fragment>
  );

  const isPlayerRoute =
    pathname.includes(`/${subject}/assessments/assignments/${assignmentId}`) &&
    assignmentId;

  const renderLDBHeaderDetails = () => {
    if (isLockdownBrowser() && isImpersonating) {
      const { studentName, sectionName } = getLDBImpersonatingHeaderDetailLC();
      return (
        <Fragment>
          <span className="Impersonate--header-info">
            You are viewing AP Classroom for
          </span>
          <div className="ldb-details">
            <span className="student-name">{studentName}</span> in{' '}
            <span className="section-name"> {sectionName} </span>
          </div>
        </Fragment>
      );
    }
    return null;
  };

  return (
    <ImpersonateFocusWrapper isPlayerRoute={isPlayerRoute}>
      <div
        className={cn(
          'Impersonate--header gap-7 bg-yellow-200 border-b-4 border-blue-500 fixed z-10 top-0',
          {
            'is-sticky': isPlayerRoute,
          },
        )}
      >
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <Fragment>
            {isLockdownBrowser() ? (
              renderLDBHeaderDetails()
            ) : (
              <Fragment>
                <span className="Impersonate--header-info">
                  You are viewing AP Classroom for:
                </span>
                <StudentsSearch
                  classes={teacherClasses}
                  onStudentSelected={onStudentSelected}
                  selectedSectionId={sectionId}
                  selectedStudentId={studentId}
                />
                <AccessibilityWrapper>
                  <a
                    role="button"
                    onClick={onLogoutClick}
                    className="Impersonate--logout"
                    tabIndex={0}
                  >
                    <span className="Impersonate--header-logout">
                      Return to my view
                    </span>{' '}
                    {icon}
                  </a>
                </AccessibilityWrapper>
              </Fragment>
            )}
          </Fragment>
        )}
      </div>
    </ImpersonateFocusWrapper>
  );
};

const mapStateToProps = ({
  featureFlag,
  impersonating,
  userData,
  subject,
}) => ({
  impersonating,
  featureFlag,
  isImpersonatingFlagAp: featureFlag.ap_teacher_impersonates_student,
  isImpersonatingFlagPreap: featureFlag.preap_teacher_impersonates_student,
  areFeatureFlagsLoaded: featureFlag.areFeatureFlagsLoaded,
  userData,
  subjectData: subject.data,
});

export default compose(
  withRouter,
  withUnitsConnectLean,
  connect(mapStateToProps),
)(ImpersonateHeader);
